import { useMemo, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Link } from 'react-router';
import { useUsers, type UserFilters } from '../hooks/useUsers';
import { UserBadges } from '../components/UserBadges';
import { SUBSCRIPTION_STATUSES } from '../lib/api-enums';

const VERDICT_TOOLTIP = 'Fills in with ASC-592 (evaluator agent). Empty by design in v0.';

function SortableHeader({
  label,
  column,
  filters,
  onChange,
}: {
  label: string;
  column: 'created_at' | 'email';
  filters: UserFilters;
  onChange: (next: UserFilters) => void;
}) {
  const active = filters.sort === column;
  return (
    <UnstyledButton
      onClick={() =>
        onChange({
          ...filters,
          sort: column,
          dir: active && filters.dir === 'desc' ? 'asc' : 'desc',
        })
      }
    >
      <Text size="sm" fw={active ? 700 : 500}>
        {label} {active ? (filters.dir === 'desc' ? '↓' : '↑') : ''}
      </Text>
    </UnstyledButton>
  );
}

export function Users() {
  const [raw, setRaw] = useState('');
  const [debounced] = useDebouncedValue(raw, 300);
  const [filters, setFilters] = useState<Omit<UserFilters, 'q'>>({
    status: null,
    showSim: false,
    sort: 'created_at',
    dir: 'desc',
  });

  const full: UserFilters = useMemo(() => ({ ...filters, q: debounced }), [filters, debounced]);
  const query = useUsers(full);
  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];

  return (
    <Stack>
      <Group align="end">
        <TextInput
          label="Search"
          placeholder="Search email or name"
          value={raw}
          onChange={(e) => setRaw(e.currentTarget.value)}
          w={280}
        />
        <Select
          label="Subscription status"
          placeholder="Any"
          clearable
          data={[...SUBSCRIPTION_STATUSES]}
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status })}
          w={200}
        />
        <Switch
          label="Show simulated users"
          checked={filters.showSim}
          onChange={(e) => setFilters({ ...filters, showSim: e.currentTarget.checked })}
        />
      </Group>

      {query.isError && (
        <Alert color="red" title="Couldn't load users">
          <Group>
            <Text size="sm">{query.error.message}</Text>
            <Button size="xs" variant="light" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </Group>
        </Alert>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <SortableHeader
                label="Email"
                column="email"
                filters={full}
                onChange={(next) => setFilters(next)}
              />
            </Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Plan</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Streak</Table.Th>
            <Table.Th>Last active</Table.Th>
            <Table.Th>
              <SortableHeader
                label="Joined"
                column="created_at"
                filters={full}
                onChange={(next) => setFilters(next)}
              />
            </Table.Th>
            <Table.Th>
              {/* Ships visible but empty. An explained empty column reads as pending;
                  an absent one reads as forgotten and gets re-litigated in three months. */}
              <Tooltip label={VERDICT_TOOLTIP} withArrow multiline w={240}>
                <Text size="sm" c="dimmed" style={{ cursor: 'help' }}>
                  Verdict
                </Text>
              </Tooltip>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.user_id}>
              <Table.Td>
                <Stack gap={2}>
                  <Anchor component={Link} to={`/users/${row.user_id}`} size="sm">
                    {row.email}
                  </Anchor>
                  <UserBadges row={row} />
                </Stack>
              </Table.Td>
              <Table.Td>{row.display_name ?? '—'}</Table.Td>
              <Table.Td>{row.subscription_tier}</Table.Td>
              <Table.Td>{row.subscription_status}</Table.Td>
              <Table.Td>{row.current_streak}</Table.Td>
              <Table.Td>
                {row.last_active_at ? new Date(row.last_active_at).toLocaleDateString() : '—'}
              </Table.Td>
              <Table.Td>{new Date(row.created_at).toLocaleDateString()}</Table.Td>
              <Table.Td c="dimmed">—</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {query.isFetching && <Loader size="sm" />}
      {!query.isFetching && rows.length === 0 && !query.isError && (
        <Text c="dimmed" size="sm">
          No users match those filters.
        </Text>
      )}
      {query.hasNextPage && (
        <Button variant="light" onClick={() => void query.fetchNextPage()}>
          Load more
        </Button>
      )}
    </Stack>
  );
}
